import { Component } from 'react';
import { HashRouter, Route, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import * as actions from './actions';
import * as actionsTs from './modules/actions';
import history from './helpers/history';
import './chartjs-setup';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import theme from './assets/css/theme';
// Styles
import core from './assets/css/core';
// CoreUI Icons Set
import '@coreui/icons/css/free.min.css';
// Import Flag Icons Set
import 'flag-icon-css/css/flag-icon.min.css';
// Import Font Awesome Icons Set
import 'font-awesome/css/font-awesome.min.css';
// Import Simple Line Icons Set
import 'simple-line-icons/dist/styles/simple-line-icons.css';
// Import Main styles for this application
import './scss/style.css';
import NewDesignBodyClass from './components/NewDesign/NewDesignBodyClass';

// Containers
import { SetupAccountLayout, WhiteLabelLayout } from './containers';
// Pages
import { Login, Register, ForgotPassword, ChangePassword } from './views/Pages';
import PublicPricing from './views/Pricing/PublicPricing';
import BookingSuite from './views/Booking/Customer/BookingSite';
import BookingSuiteConfirm from './views/Booking/Customer/BookingConfirm';
import NotificationsSystem, { dismissNotification } from 'reapop';
import { notificationTheme } from './notifications';
import StrategyModal from './elements/Strategies/StrategyModal';
import DiscountModal from './elements/User/Message/DiscountModal';
import UseDesktopModal from './elements/User/Message/UseDesktopModal';
import SurveyModalCES from './elements/User/Message/SurveyModalCES';
import SurveyModalNPS from './elements/User/Message/SurveyModalNPS';
import MessageModal from './elements/User/Message/MessageModal';
import CancelSubscriptionModal from './components/CancelSubscriptionModal';
import NewCustomerModal from './components/NewCustomerModal';
import { MobileRestrictionModal } from './components/MobileRestrictionModal';
import queryString from 'query-string';
import AppVersionModal from './components/AppVersionModal';
import { NotAuthOnlyRoute, PrivateRoute } from './components';
import { initializeHotjar } from './hotjar';
import moment from 'moment';
import { get } from 'lodash';
import { getCookie } from './helpers/cookies';
import { getTranslationKey } from './modules/utils';
import type { Notify } from './typings/redux';

const GlobalStyle = createGlobalStyle`${core}`;

interface Auth {
  authenticated?: boolean;
  email?: string;
  _id?: string;
  lastStartedStrategy?: string;
  subscription?: {
    plan?: string;
  };
  digistore?: {
    order_id?: string;
  };
  created_at?: string;
  type?: 'Basic' | 'Studio' | 'PP' | 'SB' | 'VST';
}

interface Environment {
  isEdgeOrIe: boolean;
}

interface AppProps {
  auth: Auth;
  notify: Notify;
  environment: Environment;
  notifications: any[];
  dismissNotification: (id: string) => void;
  getCurrentUser: () => Promise<string> | Promise<void>;
  getStrategy: (params: { name?: string; id?: string }) => void;
  setStrategyModalVisibility: (visible: boolean) => void;
  checkMaintenance: () => void;
  setSurveyModalVisibility: (visible: boolean) => void;
  changeModalState: (params: { key: string; state: boolean }) => void;
  setSurveyModalNPSVisibility: (visible: boolean) => void;
  accessRulesAction: () => void;
  getWatermarks: () => void;
  getUserBrands: () => void;
  getScrappbookTemplates: () => void;
  getCollectionsIds: (params: { withInitialCollectionsCount: boolean }) => void;
  setMessageModalVisibility: (params: { isMessageModalOpen: boolean; messages: string[] }) => void;
  activateNewUserModalState: () => void;
  activateMobileRestrictionModal: () => void;
  fetchEmailPackages: () => void;
}

class App extends Component<AppProps> {
  openStrategy?: string;

  newsletterConfirmed?: string;

  openSurveyModalCES?: string;

  openSurveyModalNPS?: string;

  openDowngradeModal?: string;

  destinationPath?: string;

  emailChanged?: string;

  cannyCompanyId?: string;

  cannyRedirectUrl?: string;

  zendeskSSO?: string;

  zendeskReturnTo?: string;

  destinationAfterSignIn?: string;

  intercomArg1?: string;

  intercomArg2?: Record<string, any>;

  optimalShopSettingsActivated?: string;

  optimalShopSettingsActivatedError?: string;

  productGroupActivated?: string;

  productGroupActivatedError?: string;

  constructor(props: AppProps) {
    super(props);

    const parsed = queryString.parse(history.location.search);
    const {
      openStrategy,
      newsletterConfirmed,
      openSurveyModalCES,
      openSurveyModalNPS,
      openDowngradeModal,
      destinationPath,
      emailChanged,
      companyID,
      redirect,
      zendeskSSO,
      zendeskReturnTo,
      destinationAfterSignIn,
      intercomArg1,
      intercomArg2,
      optimalShopSettingsActivated,
      optimalShopSettingsActivatedError,
      productGroupActivated,
      productGroupActivatedError
    } = parsed;

    this.openStrategy = openStrategy as string;
    this.newsletterConfirmed = newsletterConfirmed as string;
    this.openSurveyModalCES = openSurveyModalCES as string;
    this.openSurveyModalNPS = openSurveyModalNPS as string;
    this.openDowngradeModal = openDowngradeModal as string;
    this.destinationPath = destinationPath as string;
    this.emailChanged = emailChanged as string;
    this.cannyCompanyId = companyID as string;
    this.cannyRedirectUrl = redirect as string;
    this.zendeskSSO = zendeskSSO as string;
    this.zendeskReturnTo = zendeskReturnTo as string;
    this.destinationAfterSignIn = destinationAfterSignIn as string;
    this.intercomArg1 = intercomArg1 as string;
    this.intercomArg2 = intercomArg2;
    this.optimalShopSettingsActivated = optimalShopSettingsActivated as string;
    this.optimalShopSettingsActivatedError = optimalShopSettingsActivatedError as string;
    this.productGroupActivated = productGroupActivated as string;
    this.productGroupActivatedError = productGroupActivatedError as string;
  }

  async UNSAFE_componentWillMount(): Promise<void> {
    const {
      notify,
      getCurrentUser,
      getStrategy,
      setStrategyModalVisibility,
      checkMaintenance,
      setSurveyModalVisibility,
      changeModalState,
      setSurveyModalNPSVisibility,
      accessRulesAction
    } = this.props;

    const action = await getCurrentUser();

    if (action === 'redirect') return;

    checkMaintenance();
    accessRulesAction();

    if (this.openStrategy) {
      getStrategy({ name: this.openStrategy });
      setStrategyModalVisibility(true);
    }

    if (this.newsletterConfirmed === '1' && notify) {
      notify({
        title: getTranslationKey('success'),
        message: getTranslationKey('newsletterConfirmed'),
        status: 'success',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 7500
      });
    }

    if (this.optimalShopSettingsActivated === '1' && notify) {
      if (this.optimalShopSettingsActivatedError === 'false') {
        notify({
          title: getTranslationKey('success'),
          message: getTranslationKey('optimalShopSettings.message'),
          status: 'success',
          position: 'top-center',
          dismissible: true,
          dismissAfter: 1000 * 60
        });
      } else {
        notify({
          title: getTranslationKey('error'),
          message: getTranslationKey('optimalShopSettings.error'),
          status: 'error',
          position: 'top-center',
          dismissible: true
        });
      }
    }

    if (this.productGroupActivated && notify) {
      if (this.productGroupActivatedError === 'false') {
        notify({
          title: getTranslationKey('success'),
          message: getTranslationKey('productGroupActivated.message'),
          status: 'success',
          position: 'top-center',
          dismissible: true,
          dismissAfter: 1000 * 60
        });
      } else {
        notify({
          title: getTranslationKey('error'),
          message: getTranslationKey('productGroupActivated.error'),
          status: 'error',
          position: 'top-center',
          dismissible: true
        });
      }
    }

    if (this.emailChanged === '1' && notify) {
      notify({
        title: getTranslationKey('success'),
        message: getTranslationKey('changedEmailSuccessfully'),
        status: 'success',
        position: 'top-center',
        dismissible: true,
        dismissAfter: 7500
      });
    }

    if (this.openSurveyModalCES) setSurveyModalVisibility(true);
    else if (this.openSurveyModalNPS) setSurveyModalNPSVisibility(true);
    if (this.openDowngradeModal) {
      changeModalState({ key: 'featuresDowngradeModal', state: true });
    }
    if (this.destinationPath) {
      history.push(decodeURIComponent(this.destinationPath));
    }
  }

  componentDidUpdate(prevProps: AppProps): void {
    const {
      auth,
      getStrategy,
      getWatermarks,
      getUserBrands,
      getScrappbookTemplates,
      getCollectionsIds,
      environment,
      setMessageModalVisibility,
      activateNewUserModalState,
      activateMobileRestrictionModal,
      fetchEmailPackages
    } = this.props;

    if (environment.isEdgeOrIe) {
      setMessageModalVisibility({
        isMessageModalOpen: true,
        messages: [getTranslationKey('edgeOrIEWarning')]
      });
    }

    if (
      !this.openStrategy &&
      auth.lastStartedStrategy &&
      auth.lastStartedStrategy !== prevProps.auth.lastStartedStrategy
    ) {
      getStrategy({ id: auth.lastStartedStrategy });
    }

    window.setTimeout(() => {
      if (window.Intercom && this.intercomArg1) {
        window.Intercom(this.intercomArg1, this.intercomArg2);
      }
    }, 2000);

    if (
      prevProps.auth?.subscription?.plan !== auth?.subscription?.plan &&
      auth?.subscription?.plan === 'free' &&
      !auth?.digistore?.order_id &&
      new Date(auth?.created_at || 0).getTime() - new Date('2022-05-01').getTime() > 0 &&
      auth?.type === 'SB'
    ) {
      activateNewUserModalState();
    }

    if (prevProps.auth && auth._id && auth._id !== prevProps.auth._id) {
      getCollectionsIds({ withInitialCollectionsCount: true });
      getWatermarks();
      getUserBrands();
      fetchEmailPackages();

      if (this.destinationAfterSignIn) {
        window.location.href = decodeURIComponent(this.destinationAfterSignIn);
      }

      if (
        !getCookie('rescrictionModalClosed') &&
        moment(auth.created_at).isBefore(moment().subtract(7, 'days'))
      ) {
        activateMobileRestrictionModal();
      }

      if (auth.type === 'SB') {
        if (
          moment(auth.created_at).isAfter(moment().subtract(14, 'days')) &&
          get(auth, 'subscription.plan', '').includes('free')
        ) {
          initializeHotjar();
        }

        getScrappbookTemplates();

        if (this.cannyCompanyId && this.cannyRedirectUrl) {
          console.log(
            'canny parameters present =>',
            this.cannyCompanyId,
            this.cannyRedirectUrl,
            'redirect the user'
          );

          window.location.href = `${
            window.API_URL
          }/api/users/redirect/canny?companyID=${encodeURIComponent(
            this.cannyCompanyId
          )}&redirect=${encodeURIComponent(this.cannyRedirectUrl)}`;
        }

        if (this.zendeskSSO) {
          console.log('zendeskSSO parameters present =>', this.zendeskReturnTo);

          let zendeskUrl = `${window.API_URL}/api/users/redirect/zendesk`;

          if (this.zendeskReturnTo) {
            zendeskUrl += `?zendeskReturnTo=${encodeURIComponent(this.zendeskReturnTo)}`;
          }

          window.location.href = zendeskUrl;
        }

        if (window.Canny && process.env.REACT_APP_CANNY_APP_ID) {
          window.Canny('initChangelog', {
            appID: process.env.REACT_APP_CANNY_APP_ID,
            position: 'bottom',
            align: 'left'
          });
        }
      }
    }
  }

  render(): JSX.Element {
    const { auth } = this.props;
    const debugOffline = process.env.NODE_ENV !== 'production' && false;

    return (auth.authenticated && auth.email) || !auth.authenticated || debugOffline ? (
      <div
        onDrop={(e) => e.preventDefault()}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => e.preventDefault()}
      >
        <ThemeProvider theme={theme}>
          <GlobalStyle data-cy="GlobalStyle" />
          <NewDesignBodyClass />
          <StrategyModal data-cy="StrategyModal" />
          <DiscountModal data-cy="DiscountModal" />
          <SurveyModalCES data-cy="SurveyModalCES" />
          <SurveyModalNPS data-cy="SurveyModalNPS" />
          <UseDesktopModal data-cy="UseDesktopModal" />
          <MessageModal data-cy="MessageModal" />
          <AppVersionModal />
          <CancelSubscriptionModal data-cy="CancelSubscriptionModal" />
          <NewCustomerModal />
          <MobileRestrictionModal />
          <NotificationsSystem
            data-cy="NotificationsSystem"
            notifications={this.props.notifications}
            dismissNotification={(id: string) => this.props.dismissNotification(id)}
            theme={notificationTheme}
          />
          <HashRouter>
            <Switch>
              <NotAuthOnlyRoute exact path="/login" component={Login} />
              <PrivateRoute exact path="/logout" component={Login} />
              <NotAuthOnlyRoute exact path="/register" component={Register} />
              <Route exact path="/pricing" component={PublicPricing} />
              <Route path="/book/do/:id" component={BookingSuite} />
              <Route path="/book/confirm/" component={BookingSuiteConfirm} />
              <Route path="/account/forgot-password/" component={ForgotPassword} />
              <Route exact path="/account/change-password/:type?" component={ChangePassword} />
              <PrivateRoute exact path="/account/setup/" component={SetupAccountLayout} />
              <PrivateRoute path="/" component={WhiteLabelLayout} />
            </Switch>
          </HashRouter>
        </ThemeProvider>
      </div>
    ) : (
      <div className="loading">
        <div className="loader" />
      </div>
    );
  }
}

interface RootState {
  auth: Auth;
  notify: Notify;
  environment: Environment;
  notifications: any[];
}

function mapStateToProps({ auth, notify, environment, notifications }: RootState) {
  return { auth, notify, environment, notifications };
}

export default connect(mapStateToProps, { ...actions, ...actionsTs, dismissNotification })(App);
